import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import Logo from "@/components/tpr/Logo/Logo";
import RandomWallpaper from "@/components/tpr/RandomWallpaper/RandomWallpaper";
import TprIcon from "@/components/tpr/TprIcon/TprIcon";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Spinner } from "@/components/ui/spinner";
import { useLogout } from "@/hooks/useLogout";
import { tprStorage } from "@/core/storage";
import { MemberService } from "@/services/MemberService";
import type { Member } from "@/types/Member";
import { isScrolledToBottom } from "@/utils/scroll";

const LoginDeclaration = () => {
  const navigate = useNavigate();
  const logout = useLogout();
  const [qualifications, setQualifications] = useState(false);
  const [professionalBody, setProfessionalBody] = useState(false);
  const [insurance, setInsurance] = useState(false);
  const [evidence, setEvidence] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gate Confirm until the member has scrolled to the end of the declaration, in addition to
  // ticking every box. Mirrors the login terms gate ([[LoginTerms]]).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasReadDeclaration, setHasReadDeclaration] = useState(false);

  const checkScrolledToBottom = useCallback(() => {
    const el = scrollRef.current;
    // Latch on: once they've reached the bottom, stay satisfied even if they scroll back up.
    if (el && isScrolledToBottom(el)) setHasReadDeclaration(true);
  }, []);

  // Re-check on mount and on any reflow (font load / viewport resize) so a declaration short enough
  // not to scroll satisfies the gate immediately rather than trapping the member.
  useEffect(() => {
    checkScrolledToBottom();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => checkScrolledToBottom());
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkScrolledToBottom]);

  const allConfirmed = qualifications && professionalBody && insurance && evidence;

  const handleConfirm = async () => {
    if (!allConfirmed) return;
    setLoading(true);
    setError(null);
    try {
      const member = tprStorage.get<Member>("tpruser");
      if (!member) {
        setError("Session expired. Please log in again.");
        setLoading(false);
        return;
      }
      const result = await new MemberService().acceptAnnualDeclaration(member.id);
      if (result instanceof Error) {
        setError("Failed to record your declaration. Please try again.");
        setLoading(false);
        return;
      }
      // Mark the declaration accepted on the stored member (preserving terms acceptance + current
      // terms version) so the TPR route guard lets them through to the dashboard.
      tprStorage.set("tpruser", {
        ...result.member,
        annualDeclarationAccepted: true,
        termsAccepted: member.termsAccepted,
        currentTermsVersion: member.currentTermsVersion,
      });
      navigate("/dashboard", { replace: true });
    } catch {
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    await logout();
  };

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="flex justify-center min-h-screen">
        <RandomWallpaper />

        <div className="flex items-center w-full max-w-lg px-6 mx-auto lg:w-3/6 py-8">
          <div className="flex-1">
            {/* Logo */}
            <div className="text-center flex flex-col justify-center items-center">
              <div className="mb-4">
                <TprIcon size="large" />
              </div>
              <h2 className="text-4xl font-bold text-center text-gray-700 dark:text-white">
                <Logo color="black" size="large" />
              </h2>

              <p className="mt-3 text-gray-500 dark:text-gray-300">
                Before continuing, please confirm the following. This declaration is required once
                a year to keep your account active.
              </p>
            </div>

            {/* Declaration */}
            <div className="mt-6 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div
                ref={scrollRef}
                onScroll={checkScrolledToBottom}
                data-testid="declaration-scroll-region"
                className="h-80 overflow-y-auto p-4"
              >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Annual declaration
              </h3>

              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    data-testid="declaration-qualifications"
                    aria-label="I confirm I hold all the necessary UK recognised qualifications and accreditations required to practice"
                    checked={qualifications}
                    onCheckedChange={(v) => setQualifications(v === true)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    I hold all the necessary UK recognised qualifications and accreditations
                    required to practice, and these are valid and up to date.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    data-testid="declaration-professional-body"
                    aria-label="I confirm I am a member of a professional body with a formal complaints and disciplinary process"
                    checked={professionalBody}
                    onCheckedChange={(v) => setProfessionalBody(v === true)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    I am a member of a professional body with a formal complaints and disciplinary
                    process who are able to support my work in the UK.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    data-testid="declaration-insurance"
                    aria-label="I confirm I hold valid Professional Indemnity and Public Liability Insurance"
                    checked={insurance}
                    onCheckedChange={(v) => setInsurance(v === true)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    I hold valid Professional Indemnity and Public Liability Insurance appropriate
                    to my practice in the UK.
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    data-testid="declaration-evidence"
                    aria-label="I understand that The Practice Rooms may request evidence of any of the above at any time"
                    checked={evidence}
                    onCheckedChange={(v) => setEvidence(v === true)}
                    className="mt-1"
                  />
                  <span className="text-sm text-gray-800 dark:text-gray-200">
                    I understand that The Practice Rooms may request evidence of any of the above
                    at any time.
                  </span>
                </label>
              </div>

              <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                Please note: Acupuncture Therapists and Bodyworkers may need to provide a license
                to practice, depending on the regulations of the local authority governing the
                centre(s) they intend to join.
              </p>
              </div>
            </div>

            {/* Scroll gate hint — shown until the member reaches the end of the declaration */}
            {!hasReadDeclaration && (
              <p className="mt-2 text-xs text-center text-amber-600 dark:text-amber-400">
                Please scroll to the bottom of the declaration to continue.
              </p>
            )}

            {/* Error Message */}
            {error && (
              <div
                className="mt-4 p-4 text-sm text-red-700 bg-red-100 rounded-lg dark:bg-red-200 dark:text-red-800"
                role="alert"
              >
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                data-testid="declaration-confirm"
                onClick={handleConfirm}
                disabled={!allConfirmed || loading || !hasReadDeclaration}
                className="flex-1"
                aria-label="Confirm annual declaration and continue"
                title={!hasReadDeclaration ? "Scroll to the bottom of the declaration to continue" : undefined}
              >
                {loading ? <Spinner className="h-5 w-5" /> : "Confirm & continue"}
              </Button>
              <Button
                variant="secondary"
                data-testid="declaration-decline"
                onClick={handleDecline}
                disabled={loading}
                className="flex-1"
                aria-label="Decline declaration and sign out"
              >
                Decline and sign out
              </Button>
            </div>

            <p className="mt-4 text-xs text-center text-gray-400 dark:text-gray-500">
              By confirming, you declare the above statements are true. If you decline, you will be
              logged out.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginDeclaration;
